import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button } from '../components/Layout';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface ScanResult {
  type: 'order' | 'bin' | 'drop' | 'unknown';
  id?: string;
  orderNumber?: string;
  binNumber?: string;
  workOrderId?: string;
  dropId?: string;
  dropLength?: number;
  dropPartType?: string;
  dropBinLocation?: string;
  message?: string;
}

export function ScanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [manualInput, setManualInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const autoScannedRef = useRef(false);

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const tracks = (videoRef.current.srcObject as MediaStream).getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // Auto-process barcode from URL query param (from scanner routing)
  useEffect(() => {
    const barcode = searchParams.get('barcode');
    if (barcode && !autoScannedRef.current) {
      autoScannedRef.current = true;
      setManualInput(barcode);
      handleScan(barcode);
    }
  }, [searchParams]);

  const startCamera = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setCameraActive(true);
      }
    } catch (err) {
      setError('Unable to access camera. Please check permissions or use manual entry.');
      console.error('Camera error:', err);
    }
  };

  const handleScan = async (barcode: string) => {
    if (!barcode.trim()) return;

    setScanning(true);
    setError(null);
    setScanResult(null);

    try {
      const token = localStorage.getItem('auth_token');

      // Try to identify what was scanned
      // Order format: VT + YYMM + sequence (e.g., VT26010057)
      // Drop format: DRP-YYYYMMDD-XXXX (e.g., DRP-20260119-0001)
      // Bin format: BIN-XXX or just a number

      // Check if it's a drop barcode first
      if (barcode.match(/^DRP-\d{8}-\d{4}$/i)) {
        const response = await fetch(`${API_URL}/inventory/drops/by-drop-id/${barcode}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const drop = await response.json();
          setScanResult({
            type: 'drop',
            id: drop.id,
            dropId: drop.dropId,
            dropLength: drop.lengthInches,
            dropPartType: drop.partType,
            dropBinLocation: drop.binLocation,
            message: `Drop: ${drop.lengthInches}" ${drop.partType.replace(/_/g, ' ')}`,
          });
        } else {
          setScanResult({
            type: 'unknown',
            message: `Drop ${barcode} not found`,
          });
        }
      } else if (barcode.match(/^VT\d{8,}/i)) {
        // This is an order number
        const response = await fetch(`${API_URL}/orders/number/${barcode}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const order = await response.json();
          setScanResult({
            type: 'order',
            id: order.id,
            orderNumber: barcode,
            message: `Found Order: ${barcode}`,
          });
        } else {
          setScanResult({
            type: 'unknown',
            message: `Order ${barcode} not found`,
          });
        }
      } else if (barcode.match(/^BIN-?\d+$/i) || barcode.match(/^\d{3,6}$/)) {
        // This is a bin number
        const binNumber = barcode.replace(/^BIN-?/i, '');
        const response = await fetch(`${API_URL}/bins/scan/${binNumber}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const bin = await response.json();
          setScanResult({
            type: 'bin',
            id: bin.id,
            binNumber: bin.bin_number,
            workOrderId: bin.work_order_id,
            orderNumber: bin.order_number,
            message: `Bin ${bin.bin_number} - ${bin.order_number || 'Empty'}`,
          });
        } else {
          setScanResult({
            type: 'unknown',
            message: `Bin ${barcode} not found`,
          });
        }
      } else {
        // Try as a UUID (work order or order ID)
        try {
          const response = await fetch(`${API_URL}/production/work-order/${barcode}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.ok) {
            const workOrder = await response.json();
            setScanResult({
              type: 'order',
              id: workOrder.id,
              orderNumber: workOrder.order_number,
              workOrderId: workOrder.id,
              message: `Found Work Order: ${workOrder.order_number}`,
            });
          } else {
            setScanResult({
              type: 'unknown',
              message: `Unknown barcode: ${barcode}`,
            });
          }
        } catch {
          setScanResult({
            type: 'unknown',
            message: `Unknown barcode: ${barcode}`,
          });
        }
      }
    } catch (err) {
      setError('Failed to process barcode. Please try again.');
      console.error('Scan error:', err);
    } finally {
      setScanning(false);
      stopCamera();
    }
  };

  const handleNavigateToResult = () => {
    if (scanResult?.workOrderId) {
      navigate(`/work/${scanResult.workOrderId}`);
    } else if (scanResult?.id && scanResult.type === 'order') {
      navigate(`/work/${scanResult.id}`);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Barcode Scanner</h1>
        <p className="text-gray-400 mt-1">Scan order barcodes or bin labels</p>
      </div>

      {/* Camera Scanner */}
      <Card>
        <h2 className="text-lg font-semibold text-white mb-4">Camera Scan</h2>

        {!cameraActive ? (
          <div className="text-center py-8">
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-700 flex items-center justify-center">
              <svg className="w-12 h-12 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4h2v16H3V4zm4 0h1v16H7V4zm3 0h2v16h-2V4zm4 0h3v16h-3V4zm5 0h2v16h-2V4z" />
              </svg>
            </div>
            <Button onClick={startCamera} variant="primary" size="lg">
              Start Camera
            </Button>
          </div>
        ) : (
          <div className="relative">
            <video
              ref={videoRef}
              className="w-full h-64 object-cover rounded-lg bg-black"
              playsInline
              muted
            />
            <canvas ref={canvasRef} className="hidden" />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-48 border-2 border-yellow-400 rounded-lg opacity-75" />
            </div>
            <div className="mt-4 flex gap-3">
              <Button onClick={stopCamera} variant="secondary" className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  // For demo, prompt for manual entry since actual barcode detection requires a library
                  const barcode = prompt('Enter barcode (camera detection requires additional library):');
                  if (barcode) handleScan(barcode);
                }}
                variant="primary"
                className="flex-1"
              >
                Capture
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Manual Entry */}
      <Card>
        <h2 className="text-lg font-semibold text-white mb-4">Manual Entry</h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === 'Enter' && handleScan(manualInput)}
            placeholder="Enter order # (VT26010057) or bin # (BIN-001)"
            className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
          />
          <Button
            onClick={() => handleScan(manualInput)}
            disabled={!manualInput.trim() || scanning}
            variant="primary"
          >
            {scanning ? 'Scanning...' : 'Search'}
          </Button>
        </div>
      </Card>

      {/* Error Display */}
      {error && (
        <Card className="border-red-500 bg-red-500/10">
          <p className="text-red-400">{error}</p>
        </Card>
      )}

      {/* Scan Result */}
      {scanResult && (
        <Card className={scanResult.type === 'unknown' ? 'border-yellow-500' : scanResult.type === 'drop' ? 'border-blue-500' : 'border-green-500'}>
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-400 uppercase">
                {scanResult.type === 'order' ? 'Order Found' : scanResult.type === 'bin' ? 'Bin Found' : scanResult.type === 'drop' ? 'Drop Found' : 'Not Found'}
              </div>
              <div className="text-xl font-bold text-white mt-1">{scanResult.message}</div>
              {scanResult.binNumber && (
                <div className="text-gray-400 mt-1">Bin: {scanResult.binNumber}</div>
              )}
              {scanResult.type === 'drop' && (
                <div className="mt-2 space-y-1">
                  <div className="text-gray-400">
                    <span className="text-yellow-400 font-bold">{scanResult.dropId}</span>
                  </div>
                  <div className="text-gray-400">
                    Length: <span className="text-white font-medium">{scanResult.dropLength}"</span>
                  </div>
                  <div className="text-gray-400">
                    Type: <span className="text-white">{scanResult.dropPartType?.replace(/_/g, ' ')}</span>
                  </div>
                  {scanResult.dropBinLocation && (
                    <div className="text-gray-400">
                      Location: <span className="text-white">{scanResult.dropBinLocation}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {(scanResult.type === 'order' || scanResult.type === 'bin') && scanResult.workOrderId && (
              <Button onClick={handleNavigateToResult} variant="success">
                Open Work Order
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Quick Scan Buttons */}
      <Card>
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-3">
          <Button
            onClick={() => navigate('/queue')}
            variant="secondary"
            className="py-4"
          >
            My Queue
          </Button>
          <Button
            onClick={() => {
              const orderNum = prompt('Enter Order Number:');
              if (orderNum) handleScan(orderNum);
            }}
            variant="secondary"
            className="py-4"
          >
            Find Order
          </Button>
        </div>
      </Card>
    </div>
  );
}

export default ScanPage;
